/**
 * The interface and ceremony cues, synthesised: one small pure function per
 * event id, `(ctx, out, at) => void`, building its own nodes.
 *
 * Owns: what each event id sounds like. Must not: decide when it plays
 * (`sfx.ts` limits, `README.md` lists the call sites) or touch the bus. These
 * are stand-ins for recordings (TD-LL-12): replacing a cue means changing its
 * entry here and nothing else. Every cue ends within `MAX_CUE_S`, which
 * `sfx.ts` relies on to know when a voice is free.
 */
import { noise, tone } from './cue-synth';

export type CueId =
  | 'ui.press'
  | 'ui.open'
  | 'ui.close'
  | 'ui.tab'
  | 'ui.error'
  | 'reward.coin'
  | 'reward.seal'
  | 'reward.stamp'
  | 'rank.whistle'
  | 'tarp.drop'
  | 'tarp.lift'
  | 'slip.pin'
  | 'battle.victory'
  | 'battle.defeat'
  | 'battle.countdown'
  | 'battle.open_fire'
  | 'harbour.gull'
  | 'harbour.bell'
  | 'radar.ping';

export type CueBuilder = (ctx: AudioContext, out: AudioNode, at: number) => void;

/** No cue is longer than this (life-layer.md §2: envelopes ≤ 600 ms). */
export const MAX_CUE_S = 0.6;

// Pitches, Hz. Musical ones are equal-tempered note names; the rest were set by ear.
const COIN_LOW_HZ = 1568; // G6
const COIN_HIGH_HZ = 2349; // D7, a fifth above: reads as metal on metal
const SEAL_SINE_HZ = 60; // chest thump, spec
const STAMP_THUMP_HZ = 90;
const ERROR_HZ = 220; // A3
const ERROR_DETUNE_HZ = 13; // the second square sits flat of the first
const VICTORY_HZ = [392, 494, 587] as const; // G4 B4 D5, a rising major triad
const DEFEAT_HZ = [330, 247] as const; // E4 B3, a falling fourth-ish sigh
const WHISTLE_FIRST_HZ = [880, 1760] as const; // bosun's call: a rise, then a higher rise
const WHISTLE_SECOND_HZ = [1175, 2350] as const;
const BLOCK_HZ = 880; // woodblock body
const BLOCK_CLICK_HZ = 1800;
const FIRE_BOOM_HZ = [120, 40] as const;
const GULL_HZ = [[1900, 1100], [2100, 1300]] as const; // two falling cries, the second higher
const BELL_HZ = 523; // C5; the partials are the inharmonic ratios of a struck bell
const BELL_PARTIALS = [2.76, 5.4] as const;
const PING_HZ = [1320, 1180] as const; // a sonar ping: a short fall from E6, the echo a little lower and quieter
const PING_ECHO_S = 0.16;

// Lengths, seconds.
const SEAL_S = 0.18; // spec
const WHISTLE_S = 0.25;
const NOTE_GAP_S = 0.13;
const SWOOSH_DROP_S = 0.2; // spec
const SWOOSH_LIFT_S = 0.28; // spec

export const CUE_BUILDERS: Record<CueId, CueBuilder> = {
  'ui.press': (ctx, out, at) => {
    noise(ctx, out, at, { filter: 'bandpass', hz: 3000, q: 1.5, peak: 0.3, dur: 0.035 });
    tone(ctx, out, at, { type: 'sine', hz: 180, toHz: 120, peak: 0.25, dur: 0.06 });
  },
  'ui.open': (ctx, out, at) => {
    noise(ctx, out, at, { filter: 'bandpass', hz: 500, toHz: 1800, q: 1.2, peak: 0.2, dur: 0.18 });
    tone(ctx, out, at, { type: 'triangle', hz: 330, toHz: 520, peak: 0.12, dur: 0.14 });
  },
  'ui.close': (ctx, out, at) => {
    noise(ctx, out, at, { filter: 'bandpass', hz: 1800, toHz: 500, q: 1.2, peak: 0.2, dur: 0.16 });
    tone(ctx, out, at, { type: 'triangle', hz: 520, toHz: 300, peak: 0.12, dur: 0.12 });
  },
  'ui.tab': (ctx, out, at) => {
    tone(ctx, out, at, { type: 'sine', hz: 700, toHz: 560, peak: 0.25, dur: 0.05 });
    noise(ctx, out, at, { filter: 'highpass', hz: 2500, peak: 0.15, dur: 0.02 });
  },
  'ui.error': (ctx, out, at) => {
    for (const delay of [0, 0.13]) {
      tone(ctx, out, at, { type: 'square', hz: ERROR_HZ, peak: 0.12, dur: 0.09, delay });
      tone(ctx, out, at, { type: 'square', hz: ERROR_HZ - ERROR_DETUNE_HZ, peak: 0.12, dur: 0.09, delay });
    }
  },
  'reward.coin': (ctx, out, at) => {
    tone(ctx, out, at, { type: 'sine', hz: COIN_LOW_HZ, peak: 0.16, dur: 0.2 });
    tone(ctx, out, at, { type: 'sine', hz: COIN_HIGH_HZ, peak: 0.12, dur: 0.14 });
  },
  'reward.seal': (ctx, out, at) => {
    noise(ctx, out, at, { filter: 'lowpass', hz: 500, peak: 0.45, dur: SEAL_S });
    tone(ctx, out, at, { type: 'sine', hz: SEAL_SINE_HZ, peak: 0.7, dur: SEAL_S });
  },
  'reward.stamp': (ctx, out, at) => {
    tone(ctx, out, at, { type: 'sine', hz: STAMP_THUMP_HZ, toHz: 50, peak: 0.6, dur: 0.14 });
    noise(ctx, out, at, { filter: 'highpass', hz: 2500, peak: 0.3, dur: 0.025 });
  },
  'rank.whistle': (ctx, out, at) => {
    tone(ctx, out, at, { type: 'sine', hz: WHISTLE_FIRST_HZ[0], toHz: WHISTLE_FIRST_HZ[1], peak: 0.2, dur: WHISTLE_S });
    tone(ctx, out, at, { type: 'sine', hz: WHISTLE_SECOND_HZ[0], toHz: WHISTLE_SECOND_HZ[1], peak: 0.2, dur: WHISTLE_S, delay: WHISTLE_S });
  },
  'tarp.drop': (ctx, out, at) => {
    noise(ctx, out, at, { filter: 'bandpass', hz: 1800, toHz: 300, q: 0.8, peak: 0.3, dur: SWOOSH_DROP_S });
  },
  'tarp.lift': (ctx, out, at) => {
    noise(ctx, out, at, { filter: 'bandpass', hz: 300, toHz: 1800, q: 0.8, peak: 0.28, dur: SWOOSH_LIFT_S });
  },
  'slip.pin': (ctx, out, at) => {
    noise(ctx, out, at, { filter: 'highpass', hz: 4000, peak: 0.25, dur: 0.015 });
    tone(ctx, out, at, { type: 'sine', hz: 1400, peak: 0.1, dur: 0.03 });
  },
  'battle.victory': (ctx, out, at) => {
    VICTORY_HZ.forEach((hz, i) => tone(ctx, out, at, { type: 'triangle', hz, peak: 0.3, dur: i === 2 ? 0.3 : 0.2, delay: i * NOTE_GAP_S }));
  },
  'battle.defeat': (ctx, out, at) => {
    DEFEAT_HZ.forEach((hz, i) => tone(ctx, out, at, { type: 'triangle', hz, peak: 0.3, dur: 0.25, delay: i * 0.22 }));
  },
  'battle.countdown': (ctx, out, at) => {
    tone(ctx, out, at, { type: 'triangle', hz: BLOCK_HZ, toHz: BLOCK_HZ * 0.8, peak: 0.4, dur: 0.07 });
    noise(ctx, out, at, { filter: 'bandpass', hz: BLOCK_CLICK_HZ, q: 8, peak: 0.25, dur: 0.04 });
  },
  'battle.open_fire': (ctx, out, at) => {
    tone(ctx, out, at, { type: 'sine', hz: FIRE_BOOM_HZ[0], toHz: FIRE_BOOM_HZ[1], peak: 0.8, dur: 0.55 });
    noise(ctx, out, at, { filter: 'lowpass', hz: 500, toHz: 100, peak: 0.5, dur: 0.35 });
  },
  'harbour.gull': (ctx, out, at) => {
    GULL_HZ.forEach(([hz, toHz], i) => {
      tone(ctx, out, at, { type: 'triangle', hz, toHz, peak: 0.09, dur: 0.22 + i * 0.06, delay: i * 0.26 });
      noise(ctx, out, at, { filter: 'bandpass', hz: 2600, q: 2, peak: 0.03, dur: 0.18, delay: i * 0.26 });
    });
  },
  'harbour.bell': (ctx, out, at) => {
    tone(ctx, out, at, { type: 'sine', hz: BELL_HZ, peak: 0.3, dur: 0.58 });
    BELL_PARTIALS.forEach((r, i) => tone(ctx, out, at, { type: 'sine', hz: BELL_HZ * r, peak: 0.1 / (i + 1), dur: 0.35 - i * 0.12 }));
    noise(ctx, out, at, { filter: 'bandpass', hz: 3000, q: 4, peak: 0.1, dur: 0.02 });
  },
  'radar.ping': (ctx, out, at) => {
    tone(ctx, out, at, { type: 'sine', hz: PING_HZ[0], toHz: PING_HZ[1], peak: 0.16, dur: 0.42 });
    tone(ctx, out, at, { type: 'sine', hz: PING_HZ[1], toHz: PING_HZ[1] * 0.97, peak: 0.07, dur: 0.3, delay: PING_ECHO_S });
  },
};
